import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CircleArrowLeft, Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import {
  type OutgoingEmailLogListItem,
  useOutgoingEmailLogDetail,
  useOutgoingEmailLogs,
  useOutgoingEmailScenarios,
} from '~features/outgoing-email-logs/api/outgoing-email-logs-api';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import useDebounce from '~shared/hooks/useDebounce';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const columnHelper = createColumnHelper<OutgoingEmailLogListItem>();

function deliveryStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (
    status === 'delivered' ||
    status === 'accepted' ||
    status === 'processed'
  ) {
    return 'secondary';
  }
  if (
    status === 'failed' ||
    status === 'hard_bounce' ||
    status === 'dropped' ||
    status === 'blocked' ||
    status === 'spam_report'
  ) {
    return 'destructive';
  }
  return 'outline';
}

function formatDeliveryStatusLabel(status: string | undefined): string {
  if (!status?.trim()) {
    return 'Accepted';
  }
  return status.replace(/_/g, ' ');
}

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

export function OutgoingEmailLogsPage() {
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: getStoredPageSize(20),
  }));
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [scenarioFilter, setScenarioFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const resetToFirstPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const startDate = useMemo(() => {
    if (!dateRange?.from) return undefined;
    return format(dateRange.from, 'yyyy-MM-dd');
  }, [dateRange?.from]);

  const endDate = useMemo(() => {
    if (!dateRange?.to) return undefined;
    return format(dateRange.to, 'yyyy-MM-dd');
  }, [dateRange?.to]);

  const { data: scenariosData } = useOutgoingEmailScenarios();

  const scenarioMap = useMemo(() => {
    const map = new Map<string, string>();
    (scenariosData?.scenarios ?? []).forEach((s) => {
      map.set(s.value, s.label);
    });
    return map;
  }, [scenariosData?.scenarios]);

  const { data, isLoading, isError } = useOutgoingEmailLogs({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    startDate,
    endDate,
    emailScenario: scenarioFilter !== 'all' ? scenarioFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    q: debouncedSearch.trim() || undefined,
  });

  const { data: detailData, isFetching: detailLoading } =
    useOutgoingEmailLogDetail(previewId);

  const columns = useMemo(
    () => [
      columnHelper.accessor('emailScenario', {
        header: 'Type',
        cell: (info) => scenarioMap.get(info.getValue()) ?? info.getValue(),
      }),
      columnHelper.accessor('recipientEmail', {
        header: 'Recipient',
        cell: ({ row }) => (
          <div>
            <div className='font-medium'>{row.original.recipientEmail}</div>
            {row.original.recipientName ? (
              <div className='text-xs text-muted-foreground'>
                {row.original.recipientName}
              </div>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor('subject', {
        header: 'Subject',
        cell: (info) => (
          <span className='line-clamp-2'>{info.getValue() || '—'}</span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Delivery status',
        cell: (info) => (
          <Badge
            variant={deliveryStatusBadgeVariant(info.getValue() ?? 'accepted')}
          >
            {formatDeliveryStatusLabel(info.getValue())}
          </Badge>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Sent at',
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy HH:mm'),
      }),
      columnHelper.accessor('isStudioCopy', {
        header: 'Audience',
        cell: (info) => (
          <Badge variant={info.getValue() ? 'secondary' : 'outline'}>
            {info.getValue() ? 'Studio' : 'Parent'}
          </Badge>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Preview',
        cell: ({ row }) => (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => {
              setPreviewId(row.original.id);
            }}
          >
            View
          </Button>
        ),
      }),
    ],
    [scenarioMap],
  );

  return (
    <div className='min-w-0 space-y-6'>
      <Link
        to='/dashboard'
        className='flex min-w-0 items-center gap-2 text-muted-foreground transition-colors hover:text-foreground'
      >
        <CircleArrowLeft className='size-5 shrink-0' />
        <h2 className='min-w-0 text-xl font-semibold'>Outgoing email log</h2>
      </Link>

      <Card>
        <CardHeader>
          <div className='flex items-start gap-3'>
            <Mail className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
            <div className='min-w-0 space-y-2'>
              <CardTitle>Sent emails</CardTitle>
              <CardDescription className='max-w-3xl'>
                Review outgoing emails sent from the portal for your studio.
                Delivery status updates when SendGrid Event Webhooks are
                configured for this environment. Records are retained per your
                policy (typically up to six months).
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className='py-8 text-center text-muted-foreground'>
              Loading…
            </div>
          )}
          {isError && (
            <div className='py-8 text-center text-destructive'>
              Failed to load email log. Please try again.
            </div>
          )}
          {!isError && (
            <EnrollioTable
              totalLabel='Sent emails'
              filterHeader={() => (
                <>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Search
                    </p>
                    <EnrollioTableSearch
                      value={searchInput}
                      onChange={(value) => {
                        setSearchInput(value);
                        resetToFirstPage();
                      }}
                      placeholder='Search parent, student, or email...'
                      className='md:w-64'
                    />
                  </div>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Date range
                    </p>
                    <DatePickerWithRange
                      date={dateRange}
                      setDate={(r) => {
                        setDateRange(r);
                        resetToFirstPage();
                      }}
                      triggerClassName='w-full min-w-[220px] md:w-[260px]'
                    />
                  </div>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Email type
                    </p>
                    <Select
                      value={scenarioFilter}
                      onValueChange={(v) => {
                        setScenarioFilter(v);
                        resetToFirstPage();
                      }}
                    >
                      <SelectTrigger className='w-full min-w-[160px] md:w-[180px]'>
                        <SelectValue placeholder='All types' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All types</SelectItem>
                        {(scenariosData?.scenarios ?? []).map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Delivery status
                    </p>
                    <Select
                      value={statusFilter}
                      onValueChange={(v) => {
                        setStatusFilter(v);
                        resetToFirstPage();
                      }}
                    >
                      <SelectTrigger className='w-full min-w-[160px] md:w-[180px]'>
                        <SelectValue placeholder='All statuses' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All statuses</SelectItem>
                        {(scenariosData?.deliveryStatuses ?? []).map((s) => (
                          <SelectItem key={s} value={s}>
                            {formatDeliveryStatusLabel(s)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              data={data?.data ?? []}
              columns={columns}
              rowCount={data?.total ?? 0}
              manualPagination
              pagination={pagination}
              onPaginationChange={setPagination}
              getRowId={(row) => row.id}
              isUpdating={isLoading}
              emptyContent='No outgoing emails match your filters.'
              ariaLabel='Outgoing email log'
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!previewId}
        onOpenChange={(open) => {
          if (!open) setPreviewId(null);
        }}
      >
        <DialogContent className='max-h-[90vh] max-w-3xl overflow-auto'>
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
          </DialogHeader>
          {detailLoading && (
            <p className='text-sm text-muted-foreground'>Loading…</p>
          )}
          {!detailLoading && detailData && (
            <div className='space-y-4'>
              <dl className='grid grid-cols-[minmax(0,auto)_1fr] gap-x-4 gap-y-2 text-sm'>
                <dt className='font-medium text-muted-foreground'>Recipient</dt>
                <dd className='break-all'>{detailData.recipientEmail}</dd>
                <dt className='font-medium text-muted-foreground'>Subject</dt>
                <dd className='break-words'>{detailData.subject || '—'}</dd>
                <dt className='font-medium text-muted-foreground'>Sent</dt>
                <dd>
                  {detailData.createdAt
                    ? format(
                        new Date(detailData.createdAt),
                        'MMM d, yyyy HH:mm:ss',
                      )
                    : '—'}
                </dd>
                <dt className='font-medium text-muted-foreground'>Status</dt>
                <dd>
                  <Badge
                    variant={deliveryStatusBadgeVariant(
                      detailData.status ?? 'accepted',
                    )}
                  >
                    {formatDeliveryStatusLabel(detailData.status ?? 'accepted')}
                  </Badge>
                </dd>
                {detailData.statusReason ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>
                      Status detail
                    </dt>
                    <dd className='break-words text-muted-foreground'>
                      {detailData.statusReason}
                    </dd>
                  </>
                ) : null}
                {detailData.statusUpdatedAt ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>
                      Status updated
                    </dt>
                    <dd>
                      {format(
                        new Date(detailData.statusUpdatedAt),
                        'MMM d, yyyy HH:mm:ss',
                      )}
                    </dd>
                  </>
                ) : null}
              </dl>
              {detailData.bodyHtml ? (
                <div className='rounded-md border bg-muted/30 p-3'>
                  <p className='mb-2 text-xs font-medium text-muted-foreground'>
                    HTML (as captured at send time)
                  </p>
                  <iframe
                    title='Email HTML preview'
                    className='h-[min(420px,50vh)] w-full rounded border bg-white'
                    sandbox=''
                    srcDoc={detailData.bodyHtml}
                  />
                </div>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  No HTML preview stored for this message (e.g. placeholder
                  template or render unavailable at send time).
                </p>
              )}
              {detailData.bodyText ? (
                <div>
                  <p className='mb-1 text-xs font-medium text-muted-foreground'>
                    Plain text
                  </p>
                  <pre className='max-h-48 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs'>
                    {stripHtmlTags(detailData.bodyText)}
                  </pre>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
